import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { LayoutTemplate, Link2, Unlink, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  loadFolders, getLinkedFolders, getProjectLinks, linkFolderToProject, unlinkFolderFromProject,
  setProjectDefault, clearProjectDefault, setToolDisabledForProject, getProjectDefaultTemplate,
  getProjectAlternateTemplates, TEMPLATE_TYPES,
} from '@/lib/templateFolders';

/**
 * Project-linked Template Folders (section 9) — link/unlink existing
 * folders, and set/change the project's default template per Production
 * Tool. Links only ever reference a folder's id; nothing here copies a
 * folder's templates into the project.
 */
export function LinkedTemplateFoldersPanel({ creatorId, projectId }) {
  const [allFolders, setAllFolders] = useState([]);
  const [linked, setLinked] = useState([]);
  const [links, setLinks] = useState({ folderIds: [], defaults: {}, disabled: {} });
  const [linkPick, setLinkPick] = useState('');

  const refresh = () => {
    if (!creatorId || !projectId) return;
    setAllFolders(loadFolders(creatorId));
    setLinked(getLinkedFolders(creatorId, projectId));
    setLinks(getProjectLinks(projectId));
  };
  useEffect(refresh, [creatorId, projectId]);

  const unlinkedOptions = allFolders.filter((f) => !links.folderIds.includes(f.id));

  const handleLink = () => {
    if (!linkPick) return;
    linkFolderToProject(projectId, linkPick);
    setLinkPick('');
    refresh();
    toast.success('Folder linked to this project.');
  };
  const handleUnlink = (folder) => {
    unlinkFolderFromProject(projectId, folder.id);
    refresh();
    toast.success(`Unlinked "${folder.name}" — the folder itself still exists in your library.`);
  };

  const typesInUse = TEMPLATE_TYPES.filter((t) => getProjectAlternateTemplates(creatorId, projectId, t.key).length > 0);

  if (!creatorId || !projectId) return null;

  return (
    <div className="rounded-xl border border-white/10 bg-white/[0.02] p-5" data-testid="linked-template-folders-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2"><LayoutTemplate className="h-4 w-4 text-[#3A7BFF]" />Linked Template Folders</h2>
        <Link to={`/app/create/templates?projectId=${projectId}&creatorId=${creatorId}`} className="text-xs text-[#3A7BFF] hover:text-white flex items-center gap-1" data-testid="linked-folders-open-library-link">Open Templates Library<ExternalLink className="h-3 w-3" /></Link>
      </div>

      {linked.length === 0 ? (
        <p className="text-xs text-[#8A93A6] mb-3">No Template Folders linked yet — every tool will start empty for new episodes until you link one.</p>
      ) : (
        <div className="flex flex-wrap gap-2 mb-4" data-testid="linked-folders-list">
          {linked.map((f) => (
            <Badge key={f.id} variant="outline" className="border-white/10 text-[#C0C7D4] gap-1.5 pr-1" data-testid={`linked-folder-${f.id}`}>
              {f.name}
              <button onClick={() => handleUnlink(f)} className="text-[#555E6E] hover:text-red-400" title="Unlink" data-testid={`unlink-folder-${f.id}`}><Unlink className="h-3 w-3" /></button>
            </Badge>
          ))}
        </div>
      )}

      {unlinkedOptions.length > 0 && (
        <div className="flex gap-2 mb-4">
          <Select value={linkPick} onValueChange={setLinkPick}>
            <SelectTrigger className="h-8 text-xs bg-black/40 border-white/10 flex-1" data-testid="link-folder-select"><SelectValue placeholder="Link an existing Template Folder..." /></SelectTrigger>
            <SelectContent className="bg-[#1C1F26] border-white/10">{unlinkedOptions.map((f) => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" className="h-8 border-white/10 shrink-0" onClick={handleLink} disabled={!linkPick} data-testid="link-folder-confirm-btn"><Link2 className="h-3.5 w-3.5 mr-1.5" />Link</Button>
        </div>
      )}

      {typesInUse.length > 0 && (
        <div className="space-y-2 pt-3 border-t border-white/5">
          <p className="text-[11px] uppercase tracking-wide text-[#555E6E]">Defaults per Production Tool — used automatically for new episodes</p>
          {typesInUse.map((t) => {
            const alternates = getProjectAlternateTemplates(creatorId, projectId, t.key);
            const def = getProjectDefaultTemplate(creatorId, projectId, t.key);
            const disabled = !!links.disabled?.[t.key];
            return (
              <div key={t.key} className="flex items-center gap-2 flex-wrap text-xs" data-testid={`project-default-row-${t.key}`}>
                <span className="text-[#8A93A6] w-36 shrink-0">{t.label}</span>
                <Select value={disabled ? '' : (def?.entry.id || '')} onValueChange={(v) => { setProjectDefault(projectId, t.key, alternates.find((a) => a.entry.id === v)?.folder.id, v); refresh(); }} disabled={disabled}>
                  <SelectTrigger className="h-7 text-xs bg-black/40 border-white/10 flex-1 min-w-[160px]" data-testid={`project-default-select-${t.key}`}><SelectValue placeholder="Choose a template..." /></SelectTrigger>
                  <SelectContent className="bg-[#1C1F26] border-white/10">
                    {alternates.map((a) => <SelectItem key={a.entry.id} value={a.entry.id}>{a.entry.name} ({a.folder.name})</SelectItem>)}
                  </SelectContent>
                </Select>
                {def && !disabled && <Badge variant="secondary" className="bg-white/5 text-[#8A93A6] border-none">Project default</Badge>}
                <label className="flex items-center gap-1.5 text-[11px] text-[#555E6E] ml-auto shrink-0">
                  Disable
                  <Switch checked={disabled} onCheckedChange={(v) => { setToolDisabledForProject(projectId, t.key, v); if (v) clearProjectDefault(projectId, t.key); refresh(); }} data-testid={`project-default-disable-${t.key}`} />
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
